import React, { useEffect, useRef, useState } from "react";
import { IoClose, IoSettingsOutline } from "react-icons/io5";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";
import { useLiveData } from "../../../../hooks/useLiveData";
import { useRemote } from "../../../../hooks/useRemote";
import useGlobalStore from "../../../../store/global.store";
import useVipMultiviewSoundStore from "../../../../store/useVipMultiviewSoundStore";
import YouTubePlayer from "../YouTubePlayer";
import "./MultiViewLandingDisplay.css";
import {
  TOTAL_PLAYERS_DISPLAY,
  PRE_RECORDED_NAMES,
  TOP_STREAMERS,
  BLOCKED_VIDEO_IDS,
  getBackgroundByEST,
} from "../multi-view.utils";

function VipMultiViewLandingDisplay() {
  const { liveStreams, addEndedStream } = useLiveData();
  const { isMuted, toggleMute, increaseVolume, decreaseVolume } =
    useVipMultiviewSoundStore();
  const { updateView } = useRemote();

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);

  const [videoIds, setVideoIds] = useState(
    Array(TOTAL_PLAYERS_DISPLAY).fill("")
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentVideoIds, setCurrentVideoIds] = useState(
    Array(TOTAL_PLAYERS_DISPLAY).fill("")
  );
  const [recentlyEndedIds, setRecentlyEndedIds] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [showControls, setShowControls] = useState(true);

  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  const [endedIndexes, setEndedIndexes] = useState([]);
  const [allThumbnails, setAllThumbnails] = useState([]);

  const hasInteracted = useRef(false);
  const lastTapRef = useRef(0);

  useEffect(() => {
    setShowBottomBar(false);
    const interval = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleControls = () => {
    setShowControls((prev) => {
      localStorage.setItem("controlsVisible", !prev);
      return !prev;
    });
  };

  const videoIdToStreamer = Object.entries(liveStreams || {}).reduce(
    (acc, [streamer, data]) => {
      if (data?.videoId) acc[data.videoId] = data.name || streamer;
      return acc;
    },
    {}
  );

  const getSortedThumbnails = (streams) => {
    if (!streams) return [];

    return Object.entries(streams)
      .filter(
        ([name, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId)
      )
      .map(([name, data]) => ({
        streamer: data.name,
        videoId: data.videoId,
        title: data.title,
        thumbnail: data.thumbnail,
        isTop: TOP_STREAMERS.has(name),
        isLoop: PRE_RECORDED_NAMES.has(name),
      }))
      .sort((a, b) => {
        if (a.isTop !== b.isTop) return b.isTop - a.isTop;
        if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
        return 0;
      });
  };

  const getAvailableVideoIds = (currentPlaying = []) => {
    if (!liveStreams || Object.keys(liveStreams).length === 0) return [];

    const sorted = getSortedThumbnails(liveStreams);
    return sorted
      .map((v) => v.videoId)
      .filter(
        (id) =>
          !currentPlaying.includes(id) &&
          !recentlyEndedIds.includes(id) &&
          !BLOCKED_VIDEO_IDS.has(id)
      );
  };

  useEffect(() => {
    if (liveStreams && Object.keys(liveStreams).length > 0) {
      const availableIds = getAvailableVideoIds([]);

      // Make sure we don't duplicate videos across slots
      const newVideoIds = [...videoIds];
      const currentlyPlaying = new Set(newVideoIds.filter((id) => id));

      for (let i = 0; i < TOTAL_PLAYERS_DISPLAY; i++) {
        if (!newVideoIds[i] && availableIds.length > 0) {
          // Find the first available ID that's not already playing
          const nextAvailableId = availableIds.find(
            (id) => !currentlyPlaying.has(id)
          );

          if (nextAvailableId) {
            newVideoIds[i] = nextAvailableId;
            currentlyPlaying.add(nextAvailableId);
          }
        }
      }

      if (JSON.stringify(newVideoIds) !== JSON.stringify(videoIds)) {
        setVideoIds(newVideoIds);
        setCurrentVideoIds(newVideoIds);
      }
    }
  }, [liveStreams]);

  useEffect(() => {
    if (liveStreams) {
      const currentStreamIds = new Set(
        Object.values(liveStreams)
          .filter((data) => data?.videoId)
          .map((data) => data.videoId)
      );

      setAllThumbnails(getSortedThumbnails(liveStreams));

      const updatedVideoIds = [...videoIds];
      let needsUpdate = false;

      for (let i = 0; i < updatedVideoIds.length; i++) {
        const id = updatedVideoIds[i];
        if (id && !currentStreamIds.has(id)) {
          updatedVideoIds[i] = "";
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        setVideoIds(updatedVideoIds);
        setCurrentVideoIds(updatedVideoIds);

        const availableIds = getAvailableVideoIds(
          updatedVideoIds.filter((id) => id)
        );
        const finalVideoIds = [...updatedVideoIds];

        for (let i = 0; i < finalVideoIds.length; i++) {
          if (!finalVideoIds[i] && availableIds.length > 0) {
            finalVideoIds[i] = availableIds.shift();
          }
        }

        if (JSON.stringify(finalVideoIds) !== JSON.stringify(updatedVideoIds)) {
          setVideoIds(finalVideoIds);
          setCurrentVideoIds(finalVideoIds);
        }
      }
    }
  }, [liveStreams]);

  const handleSelectIndex = (index) => {
    setSelectedIndex(index);
    hasInteracted.current = true;
    setEndedIndexes((prev) => prev.filter((i) => i !== index));
  };

  const handleDoubleTap = (index) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 400;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      setFullscreenIndex((prev) => (prev === index ? null : index));
    } else {
      lastTapRef.current = now;
    }
  };

  const handleVideoSelect = (newVideoId) => {
    if (currentVideoIds.includes(newVideoId)) return;

    const updated = [...videoIds];
    updated[selectedIndex] = newVideoId;
    setVideoIds(updated);
    setCurrentVideoIds(updated);
    setEndedIndexes((prev) => prev.filter((i) => i !== selectedIndex));
  };

  const replaceVideoAtIndex = async (index, oldVideoId) => {
    try {
      // Add the ended video ID to the recently ended list
      const newRecentlyEndedIds = [
        ...new Set([...recentlyEndedIds, oldVideoId]),
      ];

      // Create a copy of the current video IDs and mark this slot as empty
      const currentPlaying = [...videoIds];
      currentPlaying[index] = "";

      // Get IDs that are already playing in other slots
      const currentlyPlayingIds = new Set(
        currentPlaying.filter((id, idx) => id && idx !== index)
      );

      // Get available videos excluding currently playing ones
      const availableIds = getAvailableVideoIds(
        currentPlaying.filter((id) => id)
      );

      // Find an available ID that's not already playing in another slot
      const uniqueAvailableId = availableIds.find(
        (id) => !currentlyPlayingIds.has(id)
      );

      // Prepare the final state update
      const updatedVideoIds = [...currentPlaying];
      let updatedEndedIndexes = [...endedIndexes.filter((i) => i !== index)];

      if (uniqueAvailableId) {
        updatedVideoIds[index] = uniqueAvailableId;
      } else {
        updatedEndedIndexes = [...updatedEndedIndexes, index];
      }

      // Update all state in a single render cycle
      setRecentlyEndedIds(newRecentlyEndedIds);
      setVideoIds(updatedVideoIds);
      setCurrentVideoIds(updatedVideoIds);
      setEndedIndexes(updatedEndedIndexes);
    } catch (err) {
      console.error("Video replace error:", err);
    }
  };

  useEffect(() => {
    if (liveStreams) {
      setAllThumbnails(getSortedThumbnails(liveStreams));
    } else {
      setAllThumbnails([]);
    }
  }, [liveStreams]);

  const handleOnVideoEnded = (videoId) => {
    console.log("onVideoEnded", videoId);
    // Add the ended video ID to the ended streams list
    addEndedStream(videoId);

    // Find the index of the video that ended
    // const index = videoIds.findIndex((id) => id === videoId);
    // if (index !== -1) {
    //   replaceVideoAtIndex(index, videoId);
    // }
  };

  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
      }}
    >
      {/* Settings Bar */}
      {!showControls && (
        <button className="gear-icon" onClick={toggleControls}>
          <IoSettingsOutline />
        </button>
      )}
      <div className={`control-bar ${showControls ? "visible" : ""}`}>
        <button
          className="control-button close-button"
          onClick={() =>
            updateView("VIP", () => {
              setShowBottomBar(true);
            })
          }
        >
          <IoClose size={34} />
        </button>
        <button
          className={`control-button ${isMuted ? "muted" : "unmuted"}`}
          onClick={toggleMute}
        >
          <LuVolumeOff size={34} />
        </button>

        <button className="control-button" onClick={increaseVolume}>
          <LuVolume2 size={34} />
        </button>
        <button className="control-button" onClick={decreaseVolume}>
          <LuVolume1 size={34} />
        </button>
      </div>

      {/* Active Players */}
      <div className="top-row">
        {Array(TOTAL_PLAYERS_DISPLAY)
          .fill()
          .map((_, index) => {
            const id = videoIds[index] || "";
            const isSelected = selectedIndex === index;
            const isFullscreen = fullscreenIndex === index;
            const showEndedOverlay = endedIndexes.includes(index) || !id;
            const streamerName = id ? videoIdToStreamer[id] || "" : "";
            const isPreRecorded =
              streamerName && PRE_RECORDED_NAMES.has(streamerName);
            const isLive =
              id &&
              Object.entries(liveStreams || {}).some(
                ([name, data]) =>
                  data?.videoId === id && !PRE_RECORDED_NAMES.has(name)
              );

            return (
              <div
                key={index}
                className={`video-container video-${index} ${
                  isFullscreen
                    ? index === 0
                      ? "fullscreen-left"
                      : "fullscreen-right"
                    : ""
                }`}
              >
                {/* <button onClick={() => handleOnVideoEnded(id)}>
                  Simulate OnEnded
                </button> */}
                <div
                  className={`video-wrapper-16x9 ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => {
                    handleSelectIndex(index);
                    handleDoubleTap(index);
                  }}
                  style={{
                    backgroundColor: "black",
                  }}
                >
                  <div className="click-capture" />
                  {id ? (
                    <YouTubePlayer
                      key={id || index}
                      videoId={id}
                      isSelected={isSelected}
                      // onEnded={() => replaceVideoAtIndex(index, id)}
                      onEnded={() => handleOnVideoEnded(id)}
                      onError={() => handleOnVideoEnded(id)}
                    />
                  ) : (
                    <div className="empty-player"></div>
                  )}
                  {streamerName && (
                    <div
                      className="streamer-label"
                      style={{
                        fontSize: 19,
                      }}
                    >
                      @{streamerName}
                    </div>
                  )}
                  {isPreRecorded ? (
                    <div
                      className="pre-recorded-badge"
                      style={{
                        fontSize: 16,
                      }}
                    >
                      PRE-RECORDED
                    </div>
                  ) : isLive ? (
                    <div
                      className="live-badge"
                      style={{
                        fontSize: 16,
                      }}
                    >
                      LIVE
                    </div>
                  ) : null}
                  {showEndedOverlay && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "black",
                        color: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "2.4rem",
                        padding: "20px",
                        textAlign: "center",
                        zIndex: 20,
                      }}
                    >
                      {id ? (
                        <>
                          This LIVE has ended.
                          <br />
                          Select Another Stream To Continue...
                        </>
                      ) : (
                        <>
                          No Live Streams Currently Available
                          <br />
                          Check Back Soon!
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Thumbnails */}
      <div className="thumbnail-scroll">
        {allThumbnails.map((stream) => {
          const disabled = currentVideoIds.includes(stream.videoId);
          return (
            <div
              key={stream.videoId}
              className={`thumb-box ${disabled ? "disabled" : ""}`}
              onClick={() => !disabled && handleVideoSelect(stream.videoId)}
            >
              <img src={stream.thumbnail} alt={stream.title} />
              <div className="thumb-meta">
                <strong
                  style={{
                    maxWidth: "180px",
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 18,
                  }}
                >
                  @{stream.streamer}
                </strong>
                <div className="badge-row">
                  {stream.isLoop ? (
                    <div
                      className="pre-recorded-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      PRE-RECORDED
                    </div>
                  ) : (
                    <div
                      className="live-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      WATCH LIVE!
                    </div>
                  )}
                </div>
                {disabled && (
                  <div
                    className="playing-label"
                    style={{
                      fontSize: 16,
                    }}
                  >
                    (Currently Playing)
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default VipMultiViewLandingDisplay;
